import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, useEffect, type FormEvent } from "react";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  FolderPlus,
  FileText,
  FileImage,
  FileSpreadsheet,
  FileType,
  File as FileIcon,
  Lock,
  MoreHorizontal,
  Move,
  Search,
  Share2,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  Undo2,
  Users,
  X,
  AlertCircle,
} from "lucide-react";
import { AppShell } from "@client/components/app-shell";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Badge } from "@client/components/ui/badge";
import { Spinner } from "@client/components/ui/spinner";
import { toast } from "@client/components/ui/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@client/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { cn } from "@client/lib/cn";
import { friendlyMime } from "@client/lib/mime";
import { useDebouncedValue } from "@client/lib/use-debounced-value";
import { ShareDocumentDialog } from "@client/components/share-document-dialog";

interface Folder {
  id: string;
  name: string;
  parentId: string | null;
  ownerId: string;
  createdAt: string;
}

interface DocItem {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  status: "pending" | "processing" | "ready" | "rejected" | "quarantined";
  ownerId: string;
  ownerName: string | null;
  isMine: boolean;
  sharesCount: number;
  folderId: string | null;
  tags: string[];
  createdAt: string;
  deletedAt: string | null;
}

async function fetchFolders(): Promise<{ folders: Folder[] }> {
  const res = await fetch("/api/folders", { credentials: "include" });
  if (!res.ok) throw new Error("Erro ao listar pastas");
  return res.json();
}

async function fetchDocs(
  folderId: string,
  trash: boolean,
  q: string,
): Promise<{ documents: DocItem[] }> {
  const params = new URLSearchParams();
  if (trash) params.set("trash", "1");
  else if (!q) params.set("folderId", folderId);
  if (q.trim().length >= 2) params.set("q", q.trim());
  const res = await fetch(`/api/documents?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("Erro ao listar documentos");
  return res.json();
}

async function createFolder(input: { name: string; parentId: string | null }): Promise<Folder> {
  const res = await fetch("/api/folders", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 409) throw new Error("Já existe pasta com esse nome aqui.");
    if (res.status === 400 && body?.error === "MaxDepthExceeded")
      throw new Error("Profundidade máxima de 8 níveis atingida.");
    throw new Error(body?.error ?? "Erro ao criar pasta.");
  }
  const data = (await res.json()) as { folder: Folder };
  return data.folder;
}

async function deleteFolder(id: string): Promise<void> {
  const res = await fetch(`/api/folders/${id}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 409) throw new Error("Pasta não está vazia.");
    throw new Error(body?.error ?? "Erro ao remover pasta.");
  }
}

async function updateDoc(
  id: string,
  patch: { name?: string; folderId?: string | null; tags?: string[] },
): Promise<void> {
  const res = await fetch(`/api/documents/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? "Erro ao atualizar documento");
  }
}

async function deleteDoc(id: string): Promise<void> {
  const res = await fetch(`/api/documents/${id}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error("Erro ao remover");
}

async function restoreDoc(id: string): Promise<void> {
  const res = await fetch(`/api/documents/${id}/restore`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erro ao restaurar");
}

export const Route = createFileRoute("/dashboard")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) throw redirect({ to: "/login" });
  },
  component: DashboardPage,
});

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelativeOrDate(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `${diffMin}m atrás`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d atrás`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function statusBadge(s: DocItem["status"]) {
  const config = {
    pending: { variant: "outline" as const, label: "Aguardando", pulse: false },
    processing: { variant: "warning" as const, label: "Verificando", pulse: true },
    ready: { variant: "success" as const, label: "Pronto", pulse: false },
    rejected: { variant: "danger" as const, label: "Rejeitado", pulse: false },
    quarantined: { variant: "danger" as const, label: "Quarentena", pulse: false },
  };
  const c = config[s];
  return (
    <Badge variant={c.variant} className={cn(c.pulse && "pulse-gold")}>
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          c.variant === "success" && "bg-[var(--color-success)]",
          c.variant === "warning" && "bg-[var(--color-warning)]",
          c.variant === "danger" && "bg-[var(--color-danger)]",
          c.variant === "outline" && "bg-[var(--color-ink-faint)]",
        )}
      />
      {c.label}
    </Badge>
  );
}

function fileIcon(mime: string) {
  if (mime.startsWith("image/")) return <FileImage className="h-4 w-4 text-[var(--color-info)]" />;
  if (mime === "application/pdf")
    return <FileText className="h-4 w-4 text-[var(--color-danger)]" />;
  if (mime.includes("spreadsheet"))
    return <FileSpreadsheet className="h-4 w-4 text-[var(--color-success)]" />;
  if (mime.includes("word") || mime.includes("document"))
    return <FileType className="h-4 w-4 text-[var(--color-info)]" />;
  return <FileIcon className="h-4 w-4 text-[var(--color-ink-muted)]" />;
}

interface FolderNode extends Folder {
  children: FolderNode[];
  depth: number;
}

function buildTree(folders: Folder[]): FolderNode[] {
  const map = new Map<string, FolderNode>();
  folders.forEach((f) => map.set(f.id, { ...f, children: [], depth: 0 }));
  const roots: FolderNode[] = [];
  map.forEach((node) => {
    if (node.parentId && map.has(node.parentId)) {
      const parent = map.get(node.parentId)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function flatten(nodes: FolderNode[]): FolderNode[] {
  const out: FolderNode[] = [];
  function recurse(list: FolderNode[]) {
    list.forEach((n) => {
      out.push(n);
      recurse(n.children);
    });
  }
  recurse(nodes);
  return out;
}

function DashboardPage() {
  const qc = useQueryClient();

  const [selectedFolderId, setSelectedFolderId] = useState<string>("root");
  const [showTrash, setShowTrash] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 250);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [editingTags, setEditingTags] = useState<DocItem | null>(null);
  const [movingDoc, setMovingDoc] = useState<DocItem | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string } | null>(null);
  const [docToDelete, setDocToDelete] = useState<DocItem | null>(null);
  const [sharingDoc, setSharingDoc] = useState<DocItem | null>(null);

  const { data: foldersData } = useQuery({ queryKey: ["folders"], queryFn: fetchFolders });
  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ["docs", selectedFolderId, showTrash, debouncedSearch],
    queryFn: () => fetchDocs(selectedFolderId, showTrash, debouncedSearch),
    refetchInterval: 5000,
    placeholderData: (prev) => prev,
  });

  const tree = useMemo(() => buildTree(foldersData?.folders ?? []), [foldersData]);
  const flat = useMemo(() => flatten(tree), [tree]);

  const createFolderMut = useMutation({
    mutationFn: createFolder,
    onSuccess: (newFolder, variables) => {
      qc.invalidateQueries({ queryKey: ["folders"] });
      if (variables.parentId) {
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(variables.parentId!);
          return next;
        });
      }
      setShowTrash(false);
      setSelectedFolderId(newFolder.id);
      toast.success(`Pasta "${newFolder.name}" criada.`);
      setNewFolderOpen(false);
    },
    onError: (err) => toast.error((err as Error).message),
  });
  const deleteFolderMut = useMutation({
    mutationFn: deleteFolder,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["folders"] });
      toast.success("Pasta removida.");
      if (selectedFolderId === id) setSelectedFolderId("root");
      setFolderToDelete(null);
    },
    onError: (err) => toast.error((err as Error).message),
  });
  const updateDocMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateDoc>[1] }) =>
      updateDoc(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["docs"] });
    },
    onError: (err) => toast.error((err as Error).message),
  });
  const deleteDocMut = useMutation({
    mutationFn: deleteDoc,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["docs"] });
      toast.success("Documento movido para a lixeira.");
      setDocToDelete(null);
    },
    onError: (err) => toast.error((err as Error).message),
  });
  const restoreDocMut = useMutation({
    mutationFn: restoreDoc,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["docs"] });
      toast.success("Documento restaurado.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const documents = docsData?.documents ?? [];
  const selectedFolder =
    selectedFolderId === "root"
      ? null
      : (foldersData?.folders ?? []).find((f) => f.id === selectedFolderId);

  const headerLabel = showTrash
    ? "Lixeira"
    : searchQuery.trim().length >= 2
      ? `Resultados para “${searchQuery}”`
      : selectedFolder
        ? selectedFolder.name
        : "Documentos na raiz";

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <AppShell
      pageEyebrow="Acervo"
      pageTitle="Documentos do escritório"
      pageActions={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button variant="secondary" asChild>
            <Link to="/documents/new-ai">
              <Sparkles className="h-4 w-4" />
              Criar doc com IA
            </Link>
          </Button>
          <Button asChild>
            <Link to="/documents/upload">
              <Upload className="h-4 w-4" />
              Enviar documento
            </Link>
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden">
            <div className="px-3 py-2.5 hairline-b flex items-center justify-between">
              <span className="text-label text-[var(--color-ink-muted)]">Pastas</span>
              <button
                type="button"
                onClick={() => setNewFolderOpen(true)}
                aria-label="Criar nova pasta"
                title="Nova pasta"
                className="flex h-7 w-7 items-center justify-center rounded-sm text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-gold)] transition-colors focus-ring"
              >
                <FolderPlus className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <ul className="py-1.5">
              <FolderRow
                label="Raiz"
                depth={0}
                active={!showTrash && selectedFolderId === "root"}
                hasChildren={tree.length > 0}
                expanded
                onClick={() => {
                  setSelectedFolderId("root");
                  setShowTrash(false);
                }}
              />
              {tree.map((node) => (
                <FolderBranch
                  key={node.id}
                  node={node}
                  expanded={expanded}
                  selectedId={showTrash ? null : selectedFolderId}
                  onToggle={toggleExpand}
                  onSelect={(id) => {
                    setSelectedFolderId(id);
                    setShowTrash(false);
                  }}
                  onDelete={(id, name) => setFolderToDelete({ id, name })}
                />
              ))}
            </ul>
            <div className="hairline-t">
              <button
                type="button"
                onClick={() => setShowTrash(true)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                  showTrash
                    ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
                    : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
                )}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Lixeira
              </button>
            </div>
          </div>

          <div className="rounded-md hairline bg-[var(--color-surface)] p-4 text-sm text-[var(--color-ink-muted)] leading-relaxed">
            <p className="text-label text-[var(--color-ink-muted)] mb-2">Dica</p>
            <p>
              Tags facilitam encontrar documentos entre pastas. A busca acima varre nomes e tags
              simultaneamente.
            </p>
          </div>
        </aside>

        <section className="space-y-4 min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-label text-[var(--color-ink-muted)]">
                {showTrash
                  ? "Soft-deleted"
                  : searchQuery.trim().length >= 2
                    ? "Busca"
                    : "Pasta atual"}
              </p>
              <h2 className="text-title text-xl text-[var(--color-ink)] mt-1">{headerLabel}</h2>
            </div>
            <div className="relative w-full sm:max-w-sm">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-ink-subtle)]"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Buscar nome ou tag…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 pl-10 text-sm"
                aria-label="Buscar documentos por nome ou tag"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1.5 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-3)] focus-ring"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
          </div>

          <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
            <div className="overflow-x-auto">
              <div className="min-w-[680px]">
                <div className="grid grid-cols-[1fr_120px_110px_130px_72px] gap-3 px-4 py-3 text-label text-[var(--color-ink-muted)] hairline-b bg-[var(--color-canvas)]">
                  <span>Documento</span>
                  <span>Tags</span>
                  <span>Status</span>
                  <span>Tamanho · Quando</span>
                  <span className="text-right">Ações</span>
                </div>

                {docsLoading && documents.length === 0 ? (
                  <div className="flex items-center gap-3 px-4 py-10 text-sm text-[var(--color-ink-muted)]">
                    <Spinner size="sm" />
                    Carregando documentos…
                  </div>
                ) : documents.length === 0 ? (
                  <EmptyState
                    trash={showTrash}
                    hasSearch={searchQuery.trim().length >= 2}
                    onUpload={!showTrash}
                  />
                ) : (
                  <ul>
                    {documents.map((d) => (
                      <li
                        key={d.id}
                        className="group relative grid grid-cols-[1fr_120px_110px_130px_72px] items-center gap-3 px-4 py-3 hairline-b last:border-b-0 transition-colors hover:bg-[var(--color-surface-2)] hover:shadow-[inset_2px_0_0_var(--color-gold)]"
                      >
                        {!showTrash && (
                          <Link
                            to="/documents/$id"
                            params={{ id: d.id }}
                            aria-label={`Abrir documento ${d.name}`}
                            className="absolute inset-0 z-10 rounded-sm focus-ring"
                          />
                        )}
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            aria-hidden
                            className="shrink-0 flex h-9 w-9 items-center justify-center rounded-sm bg-[var(--color-surface-2)] hairline"
                          >
                            {fileIcon(d.mimeType)}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm text-[var(--color-ink)] truncate font-medium">
                                {d.name}
                              </p>
                              {d.sharesCount > 0 && d.isMine && (
                                <span
                                  title={`Compartilhado com ${d.sharesCount} pessoa${d.sharesCount === 1 ? "" : "s"}`}
                                  className="shrink-0 inline-flex items-center gap-1 text-xs text-[var(--color-gold)]"
                                >
                                  <Users className="h-3 w-3" aria-hidden />
                                  {d.sharesCount}
                                </span>
                              )}
                              {!d.isMine && d.ownerName && (
                                <span
                                  title={`Compartilhado por ${d.ownerName}`}
                                  className="shrink-0 inline-flex items-center gap-1 text-xs text-[var(--color-ink-muted)]"
                                >
                                  <Share2 className="h-3 w-3" aria-hidden />
                                  de {d.ownerName.split(" ")[0]}
                                </span>
                              )}
                              {d.isMine && d.sharesCount === 0 && (
                                <Lock
                                  aria-label="Particular"
                                  className="shrink-0 h-3 w-3 text-[var(--color-ink-faint)]"
                                />
                              )}
                            </div>
                            <p className="text-xs text-[var(--color-ink-muted)] mt-0.5">
                              {friendlyMime(d.mimeType)}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1">
                          {d.tags.length > 0 ? (
                            <>
                              {d.tags.slice(0, 2).map((t) => (
                                <span
                                  key={t}
                                  className="rounded-sm bg-[var(--color-gold-wash)] px-2 py-0.5 text-xs text-[var(--color-gold-bright)] font-medium"
                                >
                                  {t}
                                </span>
                              ))}
                              {d.tags.length > 2 && (
                                <span className="text-xs text-[var(--color-ink-muted)] font-medium">
                                  +{d.tags.length - 2}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-[var(--color-ink-faint)] text-sm">—</span>
                          )}
                        </div>

                        <div>{statusBadge(d.status)}</div>

                        <div className="text-mono text-xs text-[var(--color-ink-muted)] leading-tight">
                          <div>{formatBytes(d.sizeBytes)}</div>
                          <div className="text-[var(--color-ink-subtle)] mt-0.5">
                            {formatRelativeOrDate(d.createdAt)}
                          </div>
                        </div>

                        <div className="relative z-20 flex justify-end">
                          {showTrash ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => restoreDocMut.mutate(d.id)}
                              disabled={restoreDocMut.isPending}
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                              Restaurar
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity rounded-sm p-1.5 text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink)] focus-ring"
                                  aria-label={`Ações para ${d.name}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {d.isMine && (
                                  <DropdownMenuItem onClick={() => setSharingDoc(d)}>
                                    <Share2 className="h-3.5 w-3.5" />
                                    Compartilhar…
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setEditingTags(d)}>
                                  <Tag className="h-3.5 w-3.5" />
                                  Editar etiquetas
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setMovingDoc(d)}>
                                  <Move className="h-3.5 w-3.5" />
                                  Mover para…
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem danger onClick={() => setDocToDelete(d)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Remover
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <NewFolderDialog
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        parentLabel={selectedFolder ? selectedFolder.name : "Raiz"}
        onSubmit={(name) =>
          createFolderMut.mutate({
            name,
            parentId: selectedFolderId === "root" ? null : selectedFolderId,
          })
        }
        pending={createFolderMut.isPending}
      />

      <EditTagsDialog
        doc={editingTags}
        onClose={() => setEditingTags(null)}
        onSubmit={(tags) => {
          if (!editingTags) return;
          updateDocMut.mutate(
            { id: editingTags.id, patch: { tags } },
            {
              onSuccess: () => {
                toast.success("Tags atualizadas.");
                setEditingTags(null);
              },
            },
          );
        }}
        pending={updateDocMut.isPending}
      />

      <MoveDocDialog
        doc={movingDoc}
        folders={flat}
        onClose={() => setMovingDoc(null)}
        onSubmit={(folderId) => {
          if (!movingDoc) return;
          updateDocMut.mutate(
            { id: movingDoc.id, patch: { folderId } },
            {
              onSuccess: () => {
                toast.success("Documento movido.");
                setMovingDoc(null);
              },
            },
          );
        }}
        pending={updateDocMut.isPending}
      />

      <AlertDialog
        open={!!folderToDelete}
        onOpenChange={(open) => !open && setFolderToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover pasta?</AlertDialogTitle>
            <AlertDialogDescription>
              A pasta{" "}
              <span className="text-mono text-[var(--color-ink)]">{folderToDelete?.name}</span> será
              removida. A pasta precisa estar vazia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              onClick={() => {
                if (folderToDelete) deleteFolderMut.mutate(folderToDelete.id);
              }}
            >
              {deleteFolderMut.isPending ? <Spinner size="sm" /> : "Remover pasta"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!docToDelete} onOpenChange={(open) => !open && setDocToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover para lixeira?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="text-mono text-[var(--color-ink)]">{docToDelete?.name}</span> vai
              para a lixeira. Você pode restaurar depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              onClick={() => {
                if (docToDelete) deleteDocMut.mutate(docToDelete.id);
              }}
            >
              {deleteDocMut.isPending ? <Spinner size="sm" /> : "Mover para lixeira"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareDocumentDialog
        documentId={sharingDoc?.id ?? null}
        documentName={sharingDoc?.name ?? ""}
        ownerId={sharingDoc?.ownerId ?? ""}
        onClose={() => setSharingDoc(null)}
      />
    </AppShell>
  );
}

function FolderRow({
  label,
  depth,
  active,
  hasChildren,
  expanded,
  onClick,
  onToggleExpand,
  onDelete,
}: {
  label: string;
  depth: number;
  active: boolean;
  hasChildren: boolean;
  expanded: boolean;
  onClick: () => void;
  onToggleExpand?: () => void;
  onDelete?: () => void;
}) {
  return (
    <li
      className={cn(
        "group/folder relative flex items-center gap-1.5 pr-2 cursor-pointer transition-colors",
        active
          ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
          : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
      )}
      onClick={onClick}
      style={{ paddingLeft: `${0.5 + depth * 0.85}rem` }}
    >
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-[2px] bg-[var(--color-gold)]"
        />
      )}
      {hasChildren && onToggleExpand ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="flex h-5 w-5 items-center justify-center rounded-sm text-[var(--color-ink-faint)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-3)]"
          aria-label={expanded ? "Recolher" : "Expandir"}
        >
          <ChevronRight className={cn("h-3 w-3 transition-transform", expanded && "rotate-90")} />
        </button>
      ) : (
        <span className="w-5" />
      )}
      {expanded && hasChildren ? (
        <FolderOpen className="h-3.5 w-3.5 text-[var(--color-gold)]" />
      ) : (
        <Folder className="h-3.5 w-3.5 text-[var(--color-ink-subtle)]" />
      )}
      <span className="flex-1 text-sm truncate py-1.5">{label}</span>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover/folder:opacity-100 rounded-sm p-1 text-[var(--color-ink-faint)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-danger)] transition-all"
          aria-label="Remover pasta"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </li>
  );
}

function FolderBranch({
  node,
  expanded,
  selectedId,
  onToggle,
  onSelect,
  onDelete,
}: {
  node: FolderNode;
  expanded: Set<string>;
  selectedId: string | null;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  return (
    <>
      <FolderRow
        label={node.name}
        depth={node.depth}
        active={selectedId === node.id}
        hasChildren={hasChildren}
        expanded={isExpanded}
        onClick={() => onSelect(node.id)}
        onToggleExpand={hasChildren ? () => onToggle(node.id) : undefined}
        onDelete={() => onDelete(node.id, node.name)}
      />
      {isExpanded &&
        node.children.map((child) => (
          <FolderBranch
            key={child.id}
            node={child}
            expanded={expanded}
            selectedId={selectedId}
            onToggle={onToggle}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))}
    </>
  );
}

function EmptyState({
  trash,
  hasSearch,
  onUpload,
}: {
  trash: boolean;
  hasSearch: boolean;
  onUpload: boolean;
}) {
  const title = trash
    ? "Lixeira vazia"
    : hasSearch
      ? "Nenhum documento corresponde à busca"
      : "Nada por aqui ainda";
  const desc = trash
    ? "Documentos removidos aparecem aqui e podem ser restaurados em até 30 dias."
    : hasSearch
      ? "Tente outro termo, ou limpe a busca para ver tudo."
      : "Envie o primeiro documento para começar a organizar o acervo desta pasta.";
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-md hairline bg-[var(--color-canvas)] text-[var(--color-ink-subtle)] mb-4">
        {trash ? <Trash2 className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
      </div>
      <h3 className="text-title text-base text-[var(--color-ink)]">{title}</h3>
      <p className="text-sm text-[var(--color-ink-muted)] mt-2 max-w-xs">{desc}</p>
      {onUpload && !hasSearch && (
        <Button asChild variant="secondary" size="sm" className="mt-5">
          <Link to="/documents/upload">
            <Upload className="h-3.5 w-3.5" />
            Enviar documento
          </Link>
        </Button>
      )}
    </div>
  );
}

function NewFolderDialog({
  open,
  onClose,
  parentLabel,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  parentLabel: string;
  onSubmit: (name: string) => void;
  pending: boolean;
}) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) setName("");
  }, [open]);
  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova pasta</DialogTitle>
          <DialogDescription>
            Vai ser criada dentro de{" "}
            <span className="text-mono text-[var(--color-ink-2)]">{parentLabel}</span>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Nome</Label>
            <Input
              id="folder-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex.: Contratos 2026"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={pending || name.trim().length === 0}>
              {pending ? <Spinner size="sm" /> : "Criar pasta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditTagsDialog({
  doc,
  onClose,
  onSubmit,
  pending,
}: {
  doc: DocItem | null;
  onClose: () => void;
  onSubmit: (tags: string[]) => void;
  pending: boolean;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [input, setInput] = useState("");
  useEffect(() => {
    if (doc) {
      setTags([...doc.tags]);
      setInput("");
    }
  }, [doc]);
  function addTag() {
    const t = input.trim();
    if (!t) return;
    if (tags.includes(t)) {
      setInput("");
      return;
    }
    setTags([...tags, t]);
    setInput("");
  }
  const canAdd = input.trim().length > 0 && !tags.includes(input.trim());
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Etiquetas do documento</DialogTitle>
          <DialogDescription className="truncate">{doc?.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="group inline-flex items-center gap-1.5 rounded-full bg-[var(--color-gold-wash)] border border-[var(--color-gold-dim)] pl-3 pr-1.5 py-1 text-sm text-[var(--color-gold)] font-medium"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => setTags(tags.filter((x) => x !== t))}
                    aria-label={`Remover etiqueta ${t}`}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-gold-dim)] hover:bg-[var(--color-gold)] hover:text-[var(--color-gold-ink)] transition-colors focus-ring"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div className="rounded-md hairline bg-[var(--color-surface-2)] px-4 py-5 text-center">
              <Tag className="h-5 w-5 mx-auto text-[var(--color-ink-subtle)] mb-2" aria-hidden />
              <p className="text-sm text-[var(--color-ink-muted)]">
                Nenhuma etiqueta ainda. Use etiquetas para agrupar documentos por cliente, tipo de
                processo ou assunto.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="tag-input">Adicionar etiqueta</Label>
            <div className="flex gap-2">
              <Input
                id="tag-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Ex.: trabalhista, urgente, cliente Almeida"
                autoFocus
              />
              <Button type="button" variant="secondary" onClick={addTag} disabled={!canAdd}>
                Adicionar
              </Button>
            </div>
            <p className="text-xs text-[var(--color-ink-muted)]">
              Pressione Enter para adicionar rapidamente.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => onSubmit(tags)} disabled={pending}>
            {pending ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDocDialog({
  doc,
  folders,
  onClose,
  onSubmit,
  pending,
}: {
  doc: DocItem | null;
  folders: FolderNode[];
  onClose: () => void;
  onSubmit: (folderId: string | null) => void;
  pending: boolean;
}) {
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    if (doc) setTarget(doc.folderId);
  }, [doc]);
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mover documento</DialogTitle>
          <DialogDescription className="truncate">{doc?.name}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md hairline bg-[var(--color-canvas)] max-h-72 overflow-y-auto">
          <button
            type="button"
            onClick={() => setTarget(null)}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors hairline-b",
              target === null
                ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
                : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)]",
            )}
          >
            <Folder className="h-3.5 w-3.5 text-[var(--color-ink-subtle)]" />
            <span>Raiz (sem pasta)</span>
            {target === null && (
              <span className="ml-auto text-[var(--color-gold)] text-mono text-xs font-semibold">
                ATUAL
              </span>
            )}
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setTarget(f.id)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors hairline-b last:border-b-0",
                target === f.id
                  ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
                  : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)]",
              )}
              style={{ paddingLeft: `${0.75 + f.depth * 0.9}rem` }}
            >
              <Folder className="h-3.5 w-3.5 text-[var(--color-ink-subtle)]" />
              <span className="truncate">{f.name}</span>
              {target === f.id && (
                <span className="ml-auto text-[var(--color-gold)] text-mono text-xs font-semibold">
                  SELECIONADA
                </span>
              )}
            </button>
          ))}
        </div>

        {target === doc?.folderId && (
          <div className="flex items-center gap-2 text-xs text-[var(--color-ink-muted)]">
            <AlertCircle className="h-3 w-3" />
            Documento já está nessa pasta.
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => onSubmit(target)} disabled={pending || target === doc?.folderId}>
            {pending ? <Spinner size="sm" /> : "Mover"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
